import { useTranslation } from 'react-i18next';
import type { HealthScore, StatusCounts, FindingStatus } from '../../types/data-integrity.js';
import { STATUS_LABEL_KEYS } from './IntegrityBadges.js';

/** @graph-connects none */
export function HealthScoreGauge({ health }: { health: HealthScore }) {
  const { t } = useTranslation();
  const color =
    health.score >= 95
      ? 'var(--color-success, #22c55e)'
      : health.score >= 80
        ? 'var(--color-warning, #f59e0b)'
        : health.score >= 50
          ? '#f97316'
          : 'var(--color-error, #ef4444)';

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
          <circle
            cx="18"
            cy="18"
            r="15.9155"
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="15.9155"
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeDasharray={`${String(health.score)} ${String(100 - health.score)}`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold" style={{ color }}>
            {health.score}
          </span>
        </div>
      </div>
      <div>
        <div className="text-sm font-medium text-[var(--color-text-primary)]">{health.label}</div>
        <div className="text-xs text-[var(--color-text-muted)]">
          {t('dataIntegrity.healthScoreLabel')}
        </div>
      </div>
    </div>
  );
}

/** @graph-connects none */
export function StatusCountBar({ counts }: { counts: StatusCounts }) {
  const { t } = useTranslation();
  const items: { status: FindingStatus; count: number; color: string }[] = [
    { status: 'open', count: counts.open, color: '#ef4444' },
    { status: 'acknowledged', count: counts.acknowledged, color: '#f59e0b' },
    { status: 'in_progress', count: counts.in_progress, color: '#3b82f6' },
    { status: 'resolved', count: counts.resolved, color: '#22c55e' },
    { status: 'wont_fix', count: counts.wont_fix, color: '#9ca3af' },
  ];
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex h-2 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
        {items
          .filter((i) => i.count > 0)
          .map((item) => (
            <div
              key={item.status}
              style={{
                width: `${String((item.count / total) * 100)}%`,
                backgroundColor: item.color,
              }}
            />
          ))}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        {items
          .filter((i) => i.count > 0)
          .map((item) => (
            <div key={item.status} className="flex items-center gap-1">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-[var(--color-text-muted)]">
                {t(STATUS_LABEL_KEYS[item.status])}
              </span>
              <span className="font-mono font-bold text-[var(--color-text-primary)]">
                {item.count}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}
